<template>
  <section
    class="behaviour-automated-points-rule"
    :aria-labelledby="headingId"
    :data-testid="`${testIdPrefix}-section`"
  >
    <div
      class="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2"
    >
      <h3 :id="headingId" class="behaviour-automated-points-rule__title mb-0">
        {{ title }}
      </h3>
      <CustomSwitch
        type="radio"
        :model-value="modelValue.enabled"
        :aria-label="switchAriaLabel"
        :data-testid="`${testIdPrefix}-toggle`"
        @change="onToggle"
      />
    </div>
    <p class="behaviour-automated-points-rule__description text-muted mb-3">
      {{ description }}
    </p>
    <div
      class="row g-3 behaviour-automated-points-rule__fields"
      :class="{
        'behaviour-automated-points-rule__fields--disabled': !modelValue.enabled
      }"
    >
      <div class="col-12 col-md-4">
        <label
          :id="`${testIdPrefix}-frequency-label`"
          class="v3-form-label required d-block"
          :for="`${testIdPrefix}-frequency-input`"
        >
          {{ frequencyLabel }}
        </label>
        <CustomSelect
          :model-value="modelValue.frequency"
          class="w-100"
          hide-floating-label
          :labelled-by="`${testIdPrefix}-frequency-label`"
          :options="frequencyOptions"
          :close-on-select="true"
          :searchable="false"
          :reduce="(item) => item.value"
          :disabled="!modelValue.enabled"
          :invalid-feedback="frequencyError"
          append-dropdown-to-body
          append-dropdown-fixed-position
          :input-id="`${testIdPrefix}-frequency-input`"
          :data-testid="`${testIdPrefix}-frequency`"
          @update:model-value="onFrequencyChange"
        />
      </div>
      <div class="col-12 col-md-4">
        <label
          :id="`${testIdPrefix}-core-value-label`"
          class="v3-form-label required d-block"
          :for="`${testIdPrefix}-core-value-input`"
        >
          {{ coreValueLabel }}
        </label>
        <BehaviourCoreValueLazySelect
          :model-value="modelValue.coreValueId"
          class="w-100"
          hide-floating-label
          :labelled-by="`${testIdPrefix}-core-value-label`"
          :placeholder="coreValuePlaceholder"
          :selected-option="selectedCoreValueOption"
          :disabled="!modelValue.enabled"
          :invalid-feedback="coreValueError"
          append-dropdown-to-body
          append-dropdown-fixed-position
          :input-id="`${testIdPrefix}-core-value-input`"
          :data-testid="`${testIdPrefix}-core-value`"
          @update:model-value="onCoreValueChange"
          @option-selected="onCoreValueOptionSelected"
        />
      </div>
      <div class="col-12 col-md-4">
        <label
          :id="`${testIdPrefix}-points-label`"
          class="v3-form-label required d-block"
          :for="`${testIdPrefix}-points-input`"
        >
          {{ pointsAwardedLabel }}
        </label>
        <InputField
          :id="`${testIdPrefix}-points-input`"
          class="d-block w-100"
          input-type="number"
          min-number="1"
          :model-value="pointsDisplay"
          :placeholder="resolvedPointsPlaceholder"
          :disabled="!modelValue.enabled"
          :invalid-feedback="pointsError"
          :aria-required="modelValue.enabled"
          :data-testid="`${testIdPrefix}-points`"
          @update:model-value="onPointsChange"
        />
      </div>
    </div>
  </section>
</template>

<script>
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSwitch from "@/v3components/shared/Form/CustomSwitch.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import { computed } from "vue"

export default {
  name: "AutomatedPointsRuleSection",
  components: {
    BehaviourCoreValueLazySelect,
    CustomSelect,
    CustomSwitch,
    InputField
  },
  props: {
    title: {
      type: String,
      required: true
    },
    description: {
      type: String,
      default: ""
    },
    modelValue: {
      type: Object,
      required: true
    },
    frequencyLabel: {
      type: String,
      required: true
    },
    coreValueLabel: {
      type: String,
      required: true
    },
    pointsAwardedLabel: {
      type: String,
      required: true
    },
    coreValuePlaceholder: {
      type: String,
      default: ""
    },
    pointsPlaceholder: {
      type: String,
      default: ""
    },
    frequencyOptions: {
      type: Array,
      default: () => []
    },
    switchAriaLabel: {
      type: String,
      required: true
    },
    testIdPrefix: {
      type: String,
      required: true
    },
    headingId: {
      type: String,
      required: true
    },
    fieldErrors: {
      type: Object,
      default: () => ({})
    },
    showErrors: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const pointsDisplay = computed(() => {
      const v = props.modelValue?.pointsAwarded
      return v != null && v !== "" ? String(v) : ""
    })

    /** Pre-selected `{label, value}` so the lazy select shows the saved name without a prefetch. */
    const selectedCoreValueOption = computed(() => {
      const id = props.modelValue?.coreValueId
      if (id == null || id === "") return null
      const rawName = props.modelValue?.coreValueName
      const name = rawName == null ? "" : String(rawName).trim()
      return {
        label: name || String(id),
        value: id
      }
    })

    const resolvedPointsPlaceholder = computed(() => {
      if (pointsDisplay.value) return ""
      return props.pointsPlaceholder
    })

    const showFieldErrors = computed(
      () => props.showErrors && Boolean(props.modelValue?.enabled)
    )

    const frequencyError = computed(() =>
      showFieldErrors.value ? props.fieldErrors?.frequency : ""
    )
    const coreValueError = computed(() =>
      showFieldErrors.value ? props.fieldErrors?.coreValueId : ""
    )
    const pointsError = computed(() =>
      showFieldErrors.value ? props.fieldErrors?.pointsAwarded : ""
    )

    const patchRule = (patch) => {
      emit("update:modelValue", {
        ...props.modelValue,
        ...patch
      })
    }

    const onToggle = (enabled) => {
      patchRule({ enabled: Boolean(enabled) })
    }

    const onFrequencyChange = (frequency) => {
      patchRule({ frequency: frequency || "" })
    }

    const onCoreValueChange = (coreValueId) => {
      const id =
        coreValueId != null && coreValueId !== "" ? String(coreValueId) : ""
      patchRule({ coreValueId: id })
    }

    const onCoreValueOptionSelected = (option) => {
      if (!option) return
      patchRule({
        coreValueId:
          option.value != null && option.value !== ""
            ? String(option.value)
            : "",
        coreValueName: option.label ? String(option.label) : ""
      })
    }

    const onPointsChange = (value) => {
      patchRule({ pointsAwarded: value ?? "" })
    }

    return {
      pointsDisplay,
      resolvedPointsPlaceholder,
      selectedCoreValueOption,
      frequencyError,
      coreValueError,
      pointsError,
      onToggle,
      onFrequencyChange,
      onCoreValueChange,
      onCoreValueOptionSelected,
      onPointsChange
    }
  }
}
</script>
